/**
 * AccountDeactivatedGate - Full-screen gate for deactivated accounts (Instagram-style)
 *
 * When a user's account is deactivated (profile.deleted_at is set), they cannot
 * browse the app. This gate completely replaces the app shell and only allows:
 * - Restore Account: Clears deleted_at and resumes normal access
 * - Sign Out: Logs the user out
 *
 * There is no "Later" or dismiss option - the user must choose one of these actions.
 */

import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRestoreAccount } from '../../hooks/useAccountManagement';

export function AccountDeactivatedGate() {
  const { signOut } = useAuth();
  const restoreAccount = useRestoreAccount();

  const handleRestore = () => {
    restoreAccount.mutate();
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#111111]">
      <div className="bg-[#1A1F26] rounded-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center gap-3 p-6 border-b border-white/[0.06]">
          <AlertCircle className="text-amber-400" size={24} />
          <h2 className="text-xl font-semibold text-white">Account Deactivated</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-300 mb-2">
            Your account is currently deactivated.
          </p>
          <p className="text-slate-400 text-sm mb-6">
            To continue using Filmple, please restore your account. All your data will be available again.
            If you don't restore within 30 days, your account will be permanently deleted.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleRestore}
              disabled={restoreAccount.isPending}
              className="w-full px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              {restoreAccount.isPending ? 'Restoring...' : 'Restore Account'}
            </button>
            <button
              onClick={handleSignOut}
              disabled={restoreAccount.isPending}
              className="w-full px-4 py-3 bg-[#2A2F36] hover:bg-[#3A3F46] text-slate-300 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
